/**
 * Color Memory - Saves user's color preference to sessionStorage
 * When a user selects a color on the product page, it is saved.
 * When viewing collections, products with that color will have it pre-selected.
 */

const ColorMemory = {
  STORAGE_KEY: 'ezup_selected_color',

  /**
   * Save a color to sessionStorage
   * @param {string} colorValue - The color value to save
   */
  save(colorValue) {
    if (!colorValue) return;
    try {
      sessionStorage.setItem(this.STORAGE_KEY, colorValue.toLowerCase().trim());
    } catch (e) {
      console.warn('ColorMemory: Could not save to sessionStorage', e);
    }
  },

  /**
   * Get the saved color from sessionStorage
   * @returns {string|null} - The saved color or null if not found
   */
  get() {
    try {
      return sessionStorage.getItem(this.STORAGE_KEY);
    } catch (e) {
      console.warn('ColorMemory: Could not read from sessionStorage', e);
      return null;
    }
  },

  /**
   * Clear the saved color
   */
  clear() {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
      console.warn('ColorMemory: Could not clear sessionStorage', e);
    }
  },

  /**
   * Apply saved color to collection product items
   * Finds all product items and pre-selects the saved color if available
   */
  applyToCollection() {
    this.releaseMismatchedSwatches();
    const savedColor = this.get();
    if (!savedColor) return;

    // Find all product items with color swatches
    const productItems = document.querySelectorAll('.product-item');

    productItems.forEach(productItem => {
      const swatchList = productItem.querySelector('.color-swatch-list');
      if (!swatchList) return;

      // Find all swatch radios and check for exact case-insensitive match
      const swatchRadios = swatchList.querySelectorAll('.color-swatch__radio');
      let matchingSwatch = null;

      for (const radio of swatchRadios) {
        if (radio.value.toLowerCase().trim() === savedColor) {
          matchingSwatch = radio;
          break;
        }
      }

      if (matchingSwatch && !matchingSwatch.checked) {
        // Click the swatch to trigger proper selection and image update
        matchingSwatch.click();
        this.syncImage(productItem, matchingSwatch);
      }
    });
  },

  /**
   * When a card shows a photo that isn't the selected colour (e.g. a "customize this"
   * shot), unselect the swatch so clicking that colour still swaps the photo.
   */
  releaseMismatchedSwatches() {
    document.querySelectorAll('.product-item').forEach((productItem) => {
      const checked = productItem.querySelector('.product-item__swatch-list .color-swatch__radio:checked');
      const image = productItem.querySelector('.product-item__primary-image');
      if (!checked || !image || !checked.hasAttribute('data-media-id')) return;
      if (checked.getAttribute('data-media-id') !== image.getAttribute('data-media-id')) {
        checked.checked = false;
      }
    });
  },

  /**
   * Make sure the card photo matches the selected swatch, in case the theme's
   * swatch listener was not attached yet when the swatch was clicked.
   */
  syncImage(productItem, swatch) {
    const image = productItem.querySelector('.product-item__primary-image');
    const imageUrl = swatch.getAttribute('data-image-url');
    const mediaId = swatch.getAttribute('data-media-id');
    if (!image || !imageUrl || image.getAttribute('data-media-id') === mediaId) return;

    image.removeAttribute('srcset');
    image.setAttribute('src', imageUrl);
    image.setAttribute('data-media-id', mediaId);

    const hoverUrl = swatch.getAttribute('data-image-hover-override');
    const secondary = productItem.querySelector('.product-item__secondary-image');
    const wrapper = productItem.querySelector('.product-item__image-wrapper');
    if (hoverUrl && secondary) {
      secondary.removeAttribute('srcset');
      secondary.setAttribute('src', hoverUrl);
    } else if (!hoverUrl && secondary && wrapper && !wrapper.hasAttribute('data-keep-secondary-image')) {
      secondary.remove();
      wrapper.classList.remove('product-item__image-wrapper--with-secondary');
    }

    const variantUrl = swatch.getAttribute('data-variant-url');
    if (variantUrl) {
      productItem.querySelectorAll('.product-item__image-wrapper, .product-item__title').forEach((link) => {
        link.setAttribute('href', variantUrl);
      });
    }
  },

  /**
   * Initialize color memory on product pages
   * Listens for color changes and saves them
   */
  initProductPage() {
    // Listen for color changes on the product page form
    document.addEventListener('change', (event) => {
      const target = event.target;

      // Check if this is a color swatch radio on the product form (not collection items)
      if (target.classList.contains('color-swatch__radio') &&
          target.closest('.product-form__colors')) {
        const colorValue = target.value;
        if (colorValue) {
          this.save(colorValue);
        }
      }
    });
  },

  /**
   * Initialize color memory on collection pages
   * Also saves color when user clicks swatch in collection
   */
  initCollectionPage() {
    // Apply saved color on page load
    this.applyToCollection();

    // Also save color when user clicks a swatch in the collection
    document.addEventListener('change', (event) => {
      const target = event.target;

      // Check if this is a color swatch radio in a product item (collection view)
      if (target.classList.contains('color-swatch__radio') &&
          target.closest('.product-item')) {
        const colorValue = target.value;
        if (colorValue) {
          this.save(colorValue);
        }
      }
    });
  }
};

// Auto-initialize based on page type
document.addEventListener('DOMContentLoaded', () => {
  // Check if we're on a product page
  if (document.querySelector('.product-form__colors')) {
    ColorMemory.initProductPage();
  }

  // Check if we have collection product items (could be collection page or homepage with featured products)
  if (document.querySelector('.product-item .color-swatch-list')) {
    // Wait for window load so the theme's swatch listener is attached; clicking earlier
    // checks the swatch without swapping the photo, and the card then ignores that colour.
    if (document.readyState === 'complete') {
      ColorMemory.initCollectionPage();
    } else {
      window.addEventListener('load', () => ColorMemory.initCollectionPage(), { once: true });
    }
  }
});

// Export for potential external use
window.ColorMemory = ColorMemory;
